import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import KMCaptureBanner from '../components/KMCaptureBanner';
import TyreSurveySetupBanner from '../components/TyreSurveySetupBanner';
import FuelCaptureBanner from '../components/FuelCaptureBanner';
import PPEExpiryBanner from '../components/PPEExpiryBanner';
import WellbeingDueBanner from '../components/WellbeingDueBanner';
import BreathalyzerCaptureBanner from '../components/BreathalyzerCaptureBanner';
import TelematicsCaptureBanner from '../components/TelematicsCaptureBanner';
import { 
  LayoutDashboard, 
  ClipboardList, 
  FileText, 
  Truck, 
  Users, 
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  BookOpen,
  AlertTriangle,
  AlertOctagon,
  BarChart3,
  Shield,
  Database,
  Wrench,
  CircleDot,
  Receipt,
  Fuel,
  Package,
  Replace,
  ShieldAlert,
  Wine,
  Gauge,
  MessageSquare,
  UserPlus,
  GraduationCap,
  ClipboardCheck,
  HelpCircle,
  FolderArchive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'supervisor', 'driver', 'management', 'auditor'] },
  { name: 'SOP Library', href: '/sops', icon: BookOpen, roles: ['admin', 'supervisor', 'driver', 'management', 'auditor'] },
  { name: 'Vehicles', href: '/vehicles', icon: Truck, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Daily Inspections', href: '/daily-inspections', icon: ClipboardCheck, roles: ['admin', 'supervisor', 'management', 'driver'] },
  { name: 'Drivers', href: '/drivers', icon: Users, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Planning & Loading', href: '/planning', icon: Package, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Replacement Plan', href: '/replacement-plan', icon: Replace, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Risk Management', href: '/risk-management', icon: ShieldAlert, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Breathalyzer Schedule', href: '/breathalyzer', icon: Wine, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Speeding & Fatigue', href: '/telematics', icon: Gauge, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Complaints & Compliments', href: '/complaints', icon: MessageSquare, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Recruitment', href: '/recruitment', icon: UserPlus, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Training', href: '/training', icon: GraduationCap, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Service Schedule', href: '/service-schedule', icon: Wrench, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Tyre Survey', href: '/tyre-survey', icon: CircleDot, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Breakdowns', href: '/breakdowns', icon: AlertTriangle, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Accidents', href: '/accidents', icon: AlertOctagon, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Fine Schedule', href: '/fines', icon: Receipt, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Fuel Schedule', href: '/fuel-schedule', icon: Fuel, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Schedules & Templates', href: '/schedules-templates', icon: FolderArchive, roles: ['admin', 'supervisor', 'management', 'auditor'] },
  { name: 'Corrective Actions', href: '/corrective-actions', icon: AlertTriangle, roles: ['admin', 'supervisor', 'management'] },
  { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['admin', 'management', 'auditor'] },
  { name: 'Audit Log', href: '/audit-log', icon: Shield, roles: ['admin', 'auditor', 'management'] },
  { name: 'User Manual', href: '/user-manual', icon: HelpCircle, roles: ['admin', 'supervisor', 'driver', 'management', 'auditor'] },
];

const NavItem = ({ item, isActive, onClick }) => (
  <Link
    to={item.href}
    onClick={onClick}
    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
    className={`flex items-center gap-3 px-3 py-3 rounded-sm text-sm font-medium transition-colors ${
      isActive
        ? 'bg-slate-900 text-white'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`}
  >
    <item.icon className="w-5 h-5" />
    {item.name}
  </Link>
);

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, isSupervisor, environment } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);

  const isTest = environment === 'test';
  const prefix = isTest ? '/test' : '';

  const filteredNavigation = navigation.filter(item => 
    item.roles.includes(user?.role || 'driver')
  ).map(item => ({ ...item, href: prefix + item.href }));

  const handleLogout = () => {
    logout();
    navigate(isTest ? '/test/login' : '/login');
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      supervisor: 'Supervisor',
      driver: 'Driver',
      management: 'Management',
      auditor: 'Auditor',
      master: 'Owner'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 border-r ${isTest ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
        {/* Logo */}
        <div className={`flex items-center gap-3 h-16 px-6 border-b ${isTest ? 'border-amber-200' : 'border-slate-200'}`}>
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${isTest ? 'bg-amber-500' : 'bg-slate-900'}`}>
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              RTMS PRO
            </h1>
            {isTest && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded" data-testid="test-badge">
                TEST
              </span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4 px-3">
          <nav className="space-y-1">
            {filteredNavigation.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isActive={location.pathname.startsWith(item.href)}
              />
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="border-t border-slate-200 p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="user-menu-trigger"
                className="flex items-center gap-3 w-full p-2 rounded-sm hover:bg-slate-100 transition-colors"
              >
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-slate-600">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-slate-500">{getRoleLabel(user?.role)}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-3">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <div className="flex items-center gap-3 h-16 px-6 border-b border-slate-200">
                <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  RTMS PRO
                </h1>
              </div>
              <ScrollArea className="flex-1 py-4 px-3">
                <nav className="space-y-1">
                  {filteredNavigation.map((item) => (
                    <NavItem
                      key={item.name}
                      item={item}
                      isActive={location.pathname.startsWith(item.href)}
                      onClick={() => setMobileMenuOpen(false)}
                    />
                  ))}
                </nav>
              </ScrollArea>
              <div className="border-t border-slate-200 p-4">
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full p-2 text-red-600 hover:bg-red-50 rounded-sm"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            RTMS PRO
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
            <Bell className="w-5 h-5" />
            {notifications.length > 0 && (
              <span className="notification-badge">{notifications.length}</span>
            )}
          </Button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden lg:flex lg:pl-64 h-16 bg-white border-b border-slate-200 items-center justify-between px-6 fixed top-0 right-0 left-0 z-30">
        <div className="flex items-center gap-4">
          {/* Breadcrumb or page title can go here */}
        </div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="relative" data-testid="desktop-notifications-btn">
            <Bell className="w-5 h-5" />
            {notifications.length > 0 && (
              <span className="notification-badge">{notifications.length}</span>
            )}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 min-h-screen">
        {/* Test Environment Banner */}
        {isTest && (
          <div className="bg-amber-500 text-white px-4 py-2 text-center font-bold text-sm tracking-wide sticky top-16 z-20" data-testid="test-env-banner">
            TEST ENVIRONMENT — Data in this environment is for testing only and will not affect live data
          </div>
        )}
        {/* KM Capture Banner for supervisors */}
        <KMCaptureBanner />
        {/* Tyre Survey Setup Banner for supervisors */}
        <TyreSurveySetupBanner />
        {/* Fuel Capture Banner for supervisors */}
        <FuelCaptureBanner />
        {/* PPE Expiry Banner for supervisors */}
        <PPEExpiryBanner />
        {/* Wellbeing Due Banner for supervisors */}
        <WellbeingDueBanner />
        {/* Breathalyzer Capture Banner for supervisors */}
        <BreathalyzerCaptureBanner />
        {/* Telematics Capture Banner for supervisors */}
        <TelematicsCaptureBanner />
        <div className="p-4 md:p-6 lg:p-8 pb-24 lg:pb-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav" data-testid="mobile-bottom-nav">
        <Link 
          to="/dashboard" 
          className={`mobile-nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
          data-testid="mobile-nav-dashboard"
        >
          <LayoutDashboard className="mobile-nav-icon" />
          <span className="mobile-nav-label">Home</span>
        </Link>
        <Link 
          to="/submissions" 
          className={`mobile-nav-item ${location.pathname.startsWith('/submissions') ? 'active' : ''}`}
          data-testid="mobile-nav-submissions"
        >
          <ClipboardList className="mobile-nav-icon" />
          <span className="mobile-nav-label">Forms</span>
        </Link>
        <Link 
          to="/templates" 
          className={`mobile-nav-item ${location.pathname.startsWith('/templates') ? 'active' : ''}`}
          data-testid="mobile-nav-templates"
        >
          <FileText className="mobile-nav-icon" />
          <span className="mobile-nav-label">New</span>
        </Link>
        <Link 
          to="/sops" 
          className={`mobile-nav-item ${location.pathname.startsWith('/sops') ? 'active' : ''}`}
          data-testid="mobile-nav-sops"
        >
          <BookOpen className="mobile-nav-icon" />
          <span className="mobile-nav-label">SOPs</span>
        </Link>
      </nav>
    </div>
  );
};

export default Layout;
